import React, { useCallback, useEffect, useRef, useState } from "react";
import { ACCENT } from "../artistStudio/shared";
import { apiMessage } from "./fanHomeApi";
import { TrackCardSkeleton } from "./TrackCard";
import { usePlayer } from "./PlayerContext";

const REFRESH_AFTER_MS = 2 * 60 * 1000; // a tab that comes back after this long re-checks its tracks
const REFRESH_EVERY_MS = 5 * 60 * 1000; // and a tab left open re-checks this often

// Loads one section on its own, so a failure here never stops the rest of the page.
// With { autoRefresh: true } the section also refreshes itself quietly (the cards stay on screen while it does) so a track
// that was removed or restricted disappears without a manual reload: when the tab is shown again after a while, every few
// minutes while it is open, and as soon as the player learns a track is no longer available.
export function useSection(fetcher, { autoRefresh = false } = {}) {
  const [state, setState] = useState({ status: "loading", data: null, error: "" });
  const latest = useRef(0);
  const fetchRef = useRef(fetcher);
  fetchRef.current = fetcher;

  const load = useCallback(async () => {
    const ticket = ++latest.current;
    setState((s) => ({ status: "loading", data: s.data, error: "" }));
    try {
      const data = await fetchRef.current();
      if (ticket === latest.current) setState({ status: "ready", data, error: "" });
    } catch (e) {
      if (ticket === latest.current) setState((s) => ({ status: "error", data: s.data, error: apiMessage(e, "We couldn't load this section.") }));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadedAt = useRef(Date.now());
  useEffect(() => { if (state.status === "ready") loadedAt.current = Date.now(); }, [state]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const refreshIfStale = (maxAge) => {
      if (document.visibilityState === "visible" && Date.now() - loadedAt.current >= maxAge) load();
    };
    const onVisible = () => refreshIfStale(REFRESH_AFTER_MS);
    const timer = setInterval(() => refreshIfStale(REFRESH_EVERY_MS), 60 * 1000);
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); };
  }, [autoRefresh, load]);

  // The player reported a track as no longer available (410): reload so the server drops it from the list.
  const { unavailable } = usePlayer();
  const unavailableCount = unavailable ? unavailable.size : 0;
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    if (autoRefresh && unavailableCount > 0) load();
  }, [unavailableCount, autoRefresh, load]);

  return { ...state, reload: load };
}

// Mounts its children only once the section is near the screen, and keeps them mounted afterwards, so scrolling back up
// never reloads a section that is already on the page.
export function LazyMount({ children, minHeight = 240 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(typeof IntersectionObserver === "undefined");
  useEffect(() => {
    if (visible || !ref.current) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) { setVisible(true); observer.disconnect(); }
    }, { rootMargin: "400px 0px" });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [visible]);
  return <div ref={ref} style={visible ? undefined : { minHeight }}>{visible ? children : null}</div>;
}

export function SectionFrame({ title, subtitle, id, children }) {
  return (
    <section aria-labelledby={id} className="mt-10">
      <div className="mb-3">
        <h2 id={id} className="text-base font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}

// Four cards across on a wide screen, as in the design. ROW is the sideways strip of fixed-width cards.
export const CARD_GRID = "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4";
export const CARD_ROW = "flex gap-4 overflow-x-auto pb-2";

export function SkeletonGrid({ count = 4, row = false }) {
  return (
    <div className={row ? CARD_ROW : CARD_GRID} role="status" aria-label="Loading">
      {Array.from({ length: count }).map((_, i) => <TrackCardSkeleton key={i} compact={row} />)}
    </div>
  );
}

export function SectionError({ message, onRetry }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-gray-50 px-5 py-6 text-center" role="alert">
      <p className="text-gray-700 mb-3">{message}</p>
      <button type="button" onClick={onRetry} className="rounded-full font-semibold text-white px-5 py-2 text-sm" style={{ backgroundColor: ACCENT }}>Try again</button>
    </div>
  );
}

export function EmptyNote({ children }) {
  return <p className="rounded-2xl border border-dashed border-gray-200 px-5 py-8 text-center text-gray-500">{children}</p>;
}
